import React, { useState } from 'react';
import { X, Zap, Scale, Brain, Clock, DollarSign, ChevronRight, ChevronDown, Sparkles, Bot } from 'lucide-react';

export type AIProvider = 'anthropic' | 'openai';

export interface ModelInfo {
  id: string;
  name: string;
  modelId: string;
  description: string;
  speed: 'Fast' | 'Balanced' | 'Thorough';
  cost: 'Low' | 'Medium' | 'High';
  bestFor: string;
  provider: AIProvider;
}

export interface SelectedModel {
  provider: AIProvider;
  modelId: string;
  modelName: string;
}

// Claude Models
export const CLAUDE_MODELS: ModelInfo[] = [
  {
    id: 'haiku',
    name: 'Claude Haiku 4.5',
    modelId: 'claude-haiku-4-5-20251001',
    description: 'Fastest model for quick analysis',
    speed: 'Fast',
    cost: 'Low',
    bestFor: 'Quick reviews, simple contracts, preliminary screening',
    provider: 'anthropic'
  },
  {
    id: 'sonnet',
    name: 'Claude Sonnet 5.5',
    modelId: 'claude-sonnet-5-5',
    description: 'Balanced performance and quality',
    speed: 'Balanced',
    cost: 'Medium',
    bestFor: 'Standard contracts, routine analysis, good balance of speed and detail',
    provider: 'anthropic'
  },
  {
    id: 'opus',
    name: 'Claude Opus 5.5',
    modelId: 'claude-opus-5-5',
    description: 'Most thorough and detailed analysis',
    speed: 'Thorough',
    cost: 'High',
    bestFor: 'Complex contracts, high-value deals, detailed legal analysis',
    provider: 'anthropic'
  }
];

// OpenAI GPT-5 Models
export const OPENAI_MODELS: ModelInfo[] = [
  {
    id: 'gpt5-mini',
    name: 'GPT-5 Mini',
    modelId: 'gpt-5-mini-2025-08-07',
    description: 'Quick and budget-friendly variant',
    speed: 'Fast',
    cost: 'Low',
    bestFor: 'Clear tasks, precise prompts, quick turnaround',
    provider: 'openai'
  },
  {
    id: 'gpt41',
    name: 'GPT-4.1',
    modelId: 'gpt-4.1',
    description: 'Larger context and stronger reasoning than Mini',
    speed: 'Balanced',
    cost: 'Medium',
    bestFor: 'General contract analysis where Mini is too shallow',
    provider: 'openai'
  },
  {
    id: 'gpt51',
    name: 'GPT-5.1',
    modelId: 'gpt-5.1',
    description: 'Most capable OpenAI model available here',
    speed: 'Thorough',
    cost: 'High',
    bestFor: 'Complex contracts when comparing against Claude',
    provider: 'openai'
  }
];

/*
 * Only models this organisation can actually reach are listed.
 *
 * gpt-5-1 used to be listed and returned 404: the real ID is gpt-5.1. gpt-5-pro
 * exists but is Responses-API only, and the backend uses chat completions.
 *
 * Opus 5.5 and Sonnet 5.5 always think; the backend caps their reasoning with
 * an explicit effort level (backend/src/config/aiModels.ts).
 */

interface ModelSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectModel: (selection: SelectedModel) => void;
  isLoading?: boolean;
}

export const ModelSelectionModal: React.FC<ModelSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectModel,
  isLoading = false
}) => {
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('anthropic');
  const [selectedClaudeModel, setSelectedClaudeModel] = useState<string>('sonnet');
  const [selectedOpenAIModel, setSelectedOpenAIModel] = useState<string>('gpt5');
  const [showClaudeDropdown, setShowClaudeDropdown] = useState(false);
  const [showOpenAIDropdown, setShowOpenAIDropdown] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedProvider === 'anthropic') {
      const model = CLAUDE_MODELS.find(m => m.id === selectedClaudeModel);
      if (model) {
        onSelectModel({
          provider: 'anthropic',
          modelId: model.modelId,
          modelName: model.name
        });
      }
    } else {
      const model = OPENAI_MODELS.find(m => m.id === selectedOpenAIModel);
      if (model) {
        onSelectModel({
          provider: 'openai',
          modelId: model.modelId,
          modelName: model.name
        });
      }
    }
  };

  const getSpeedBadge = (speed: string) => {
    const colors = {
      'Fast': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'Balanced': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'Thorough': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[speed as keyof typeof colors]}`}>
        {speed}
      </span>
    );
  };

  const getCostBadge = (cost: string) => {
    const colors = {
      'Low': 'text-green-600 dark:text-green-400',
      'Medium': 'text-blue-600 dark:text-blue-400',
      'High': 'text-purple-600 dark:text-purple-400'
    };
    const dollars = cost === 'Low' ? 1 : cost === 'Medium' ? 2 : 3;
    return (
      <span className={`flex items-center ${colors[cost as keyof typeof colors]}`}>
        {Array(dollars).fill(0).map((_, i) => (
          <DollarSign key={i} className="w-3 h-3 -ml-0.5 first:ml-0" />
        ))}
      </span>
    );
  };

  const currentClaudeModel = CLAUDE_MODELS.find(m => m.id === selectedClaudeModel);
  const currentOpenAIModel = OPENAI_MODELS.find(m => m.id === selectedOpenAIModel);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Select AI Model
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Choose your preferred AI provider and model for contract analysis
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Provider Selection */}
        <div className="p-6 space-y-6">
          {/* Provider Cards */}
          <div className="grid grid-cols-2 gap-4">
            {/* Anthropic Claude Card */}
            <div
              onClick={() => setSelectedProvider('anthropic')}
              className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedProvider === 'anthropic'
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                  : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${
                  selectedProvider === 'anthropic'
                    ? 'bg-orange-100 dark:bg-orange-900/30'
                    : 'bg-gray-100 dark:bg-gray-800'
                }`}>
                  <Sparkles className={`w-5 h-5 ${
                    selectedProvider === 'anthropic' ? 'text-orange-600' : 'text-gray-500'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Anthropic</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Claude AI</p>
                </div>
              </div>

              {/* Claude Model Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProvider('anthropic');
                    setShowClaudeDropdown(!showClaudeDropdown);
                    setShowOpenAIDropdown(false);
                  }}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    selectedProvider === 'anthropic'
                      ? 'bg-white dark:bg-dark-surface border-orange-300 dark:border-orange-700'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">
                      {currentClaudeModel?.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentClaudeModel && getSpeedBadge(currentClaudeModel.speed)}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showClaudeDropdown ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {showClaudeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-10 overflow-hidden">
                    {CLAUDE_MODELS.map((model) => (
                      <button
                        key={model.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClaudeModel(model.id);
                          setShowClaudeDropdown(false);
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          selectedClaudeModel === model.id ? 'bg-orange-50 dark:bg-orange-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm text-gray-900 dark:text-white">{model.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{model.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getSpeedBadge(model.speed)}
                            {getCostBadge(model.cost)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedProvider === 'anthropic' && (
                <div className="absolute top-2 right-2 w-3 h-3 bg-orange-500 rounded-full" />
              )}
            </div>

            {/* OpenAI Card */}
            <div
              onClick={() => setSelectedProvider('openai')}
              className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedProvider === 'openai'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                  : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${
                  selectedProvider === 'openai'
                    ? 'bg-emerald-100 dark:bg-emerald-900/30'
                    : 'bg-gray-100 dark:bg-gray-800'
                }`}>
                  <Bot className={`w-5 h-5 ${
                    selectedProvider === 'openai' ? 'text-emerald-600' : 'text-gray-500'
                  }`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">OpenAI</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">GPT-5 Models</p>
                </div>
              </div>

              {/* OpenAI Model Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProvider('openai');
                    setShowOpenAIDropdown(!showOpenAIDropdown);
                    setShowClaudeDropdown(false);
                  }}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    selectedProvider === 'openai'
                      ? 'bg-white dark:bg-dark-surface border-emerald-300 dark:border-emerald-700'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">
                      {currentOpenAIModel?.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentOpenAIModel && getSpeedBadge(currentOpenAIModel.speed)}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showOpenAIDropdown ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {showOpenAIDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-10 overflow-hidden">
                    {OPENAI_MODELS.map((model) => (
                      <button
                        key={model.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOpenAIModel(model.id);
                          setShowOpenAIDropdown(false);
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          selectedOpenAIModel === model.id ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm text-gray-900 dark:text-white">{model.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{model.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getSpeedBadge(model.speed)}
                            {getCostBadge(model.cost)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedProvider === 'openai' && (
                <div className="absolute top-2 right-2 w-3 h-3 bg-emerald-500 rounded-full" />
              )}
            </div>
          </div>

          {/* Selected Model Details */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${
                selectedProvider === 'anthropic'
                  ? 'bg-orange-100 dark:bg-orange-900/30'
                  : 'bg-emerald-100 dark:bg-emerald-900/30'
              }`}>
                {selectedProvider === 'anthropic'
                  ? <Sparkles className="w-5 h-5 text-orange-600" />
                  : <Bot className="w-5 h-5 text-emerald-600" />
                }
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {selectedProvider === 'anthropic' ? currentClaudeModel?.name : currentOpenAIModel?.name}
                  </h4>
                  {selectedProvider === 'anthropic' && currentClaudeModel && (
                    <>
                      {getSpeedBadge(currentClaudeModel.speed)}
                      {getCostBadge(currentClaudeModel.cost)}
                    </>
                  )}
                  {selectedProvider === 'openai' && currentOpenAIModel && (
                    <>
                      {getSpeedBadge(currentOpenAIModel.speed)}
                      {getCostBadge(currentOpenAIModel.cost)}
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedProvider === 'anthropic' ? currentClaudeModel?.description : currentOpenAIModel?.description}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  <span className="font-medium">Best for:</span>{' '}
                  {selectedProvider === 'anthropic' ? currentClaudeModel?.bestFor : currentOpenAIModel?.bestFor}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Analysis time varies based on contract complexity and model selection
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex items-center gap-2 px-6 py-2 text-white rounded-lg transition-colors ${
                selectedProvider === 'anthropic'
                  ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400'
                  : 'bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  Start Analysis
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelSelectionModal;
